<template>
  <div
    :class="[
      'scrollable-x',
      $style.container,
      !bordered && $style.containerNoborder,
      $style[`container${themeName}`]
    ]"
    ref="container"
  >
    <button
      v-if="showNav"
      @click="prev"
      :class="[
        $style.button,
        $style.buttonPrev,
        !prevActive && $style.buttonInactive
      ]"
    >
      <b-svg
        name="arrow-left"
        :height="24"
      />
    </button>

    <div
      :class="$style.content"
      ref="content"
      @scroll="onScroll"
    >
      <ul
        :class="[$style.tabs, $style[`tabs${themeName}`]]"
        ref="tabs"
      >
        <li
          v-for="(tab, index) in filteredTabs"
          :class="[
            $style.tab,
            $style[`tab${themeName}`],
            tab.invalid && $style.invalid,
            isActive(tab) && $style[`tab${themeName}Active`]
          ]"
          :key="`tab-${index}`"
          @click="switchTab(tab, $event, `tab-${index}`)"
          @keyup.enter="switchTab(tab, $event, `tab-${index}`)"
          :tabindex="!isActive(tab) ? 0 : -1"
          :ref="`tab-${index}`"
          :data-testid="tab.value"
        >
          <slot
            name="tab"
            :tab="tab"
          >
            {{ t(tab.label) }}
          </slot>
          <span v-if="tab.indicator" :class="$style.tabIndicator" />
          <span v-if="tab.valueInCircle" :class="$style.tabValueInCircle" >{{tab.valueInCircle}}</span>
        </li>
      </ul>
    </div>

    <button
      v-if="showNav"
      @click="next"
      :class="[
        $style.button,
        $style.buttonNext,
        !nextActive && $style.buttonInactive
      ]"
    >
      <b-svg
        name="arrow-right"
        :size="24"
      />
    </button>
  </div>
</template>

<script>
import { useCore } from '@/composables/core'
import { findLast, map, pickBy, compose, prop, values, find } from 'ramda'
const SCROLL_BEHAVIOR = 'smooth'

export default {

  emits: [
    'switch-tab',
  ],
  setup () {
    const { t } = useCore()

    return { t }
  },

  data () {
    return {
      showNav: false,
      prevActive: false,
      nextActive: true,
    }
  },

  props: {
    /*
     * tabs: [{ name, value, active: true }]
     */
    tabs: {
      type: Array,
      required: true,
    },
    activeTab: {
      type: Object,
      required: true,
    },
    theme: {
      type: String,
      default: 'default',
    },
    bordered: {
      type: Boolean,
      default: true,
    },
  },

  methods: {
    switchTab (tab, e, ref) {
      const content = this.$refs.content.getBoundingClientRect()
      const el = this.$refs[ref][0].getBoundingClientRect()

      if (el.x < content.x) {
        this.$refs.content.scrollBy({
          left: el.x - content.x,
          behavior: SCROLL_BEHAVIOR,
        })
      } else if (el.x + el.width > content.x + content.width) {
        this.$refs.content.scrollBy({
          left: el.width - (content.x + content.width - el.x),
          behavior: SCROLL_BEHAVIOR,
        })
      }
      this.$emit('switch-tab', tab, e)
    },
    isActive (tab) {
      if (this.activeTab.id) {
        return this.activeTab.id === tab.id
      }
      return this.activeTab.value === tab.value
    },
    capitalize (text) {
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    prev () {
      if (!this.prevActive) return
      const firstVisibleTab = compose(
        find((val) => val.offsetLeft >= this.$refs.content.scrollLeft),
        map(prop('0')),
        values,
        pickBy((_, key) => key.includes('tab-'))
      )(this.$refs)
      this.$refs.content.scrollTo({
        left: firstVisibleTab.offsetLeft + firstVisibleTab.offsetWidth - this.$refs.content.offsetWidth,
        behavior: SCROLL_BEHAVIOR,
      })
    },
    next () {
      if (!this.nextActive) return
      const lastVisibleTab = compose(
        findLast((val) => val.offsetLeft + val.offsetWidth <= this.$refs.container.offsetWidth + this.$refs.content.scrollLeft),
        map(prop('0')),
        values,
        pickBy((_, key) => key.includes('tab-'))
      )(this.$refs)
      this.$refs.content.scrollTo({
        left: lastVisibleTab.offsetLeft,
        behavior: SCROLL_BEHAVIOR,
      })
    },
    onScroll (e) {
      this.prevActive = e.target.scrollLeft !== 0
      const offset = this.$refs.tabs.getBoundingClientRect().width - this.$refs.content.getBoundingClientRect().width
      this.nextActive = Math.round(offset) !== e.target.scrollLeft
    },
    clearScroll () {
      this.$refs.content.scroll(0, 0)
    },
    init () {
      this.showNav = this.theme.toLowerCase() !== 'bordered'
        ? this.$refs.tabs.getBoundingClientRect().width > this.$refs.container.getBoundingClientRect().width
        : false
    },
  },

  computed: {
    themeName () {
      return this.capitalize(this.theme)
    },

    filteredTabs () {
      return this.tabs.filter(tab => {
        return !tab.isHidden
      })
    },
  },

  mounted () {
    this.init()
  },

  watch: {
    tabs () {
      this.$nextTick(this.init)
    },
  },
}
</script>

<style lang="scss" module>
@layer legacy-views {

@import '@/assets/stylesheets/variables';

.container {
  display: flex;
  align-items: center;
  overflow: hidden;
  position: relative;

  &Default {
    border-bottom: 1px solid var(--color-border-primary);
    height: 48px;
  }

  &Noborder {
    margin-bottom: -1px;
  }
}

.content {
  position: relative;
  flex: 1;
  white-space: nowrap;
  overflow-x: auto;
  height: 100%;

  &::-webkit-scrollbar {
    height: 0;
    width: 0;
  }
}

.button {
  cursor: pointer;

  //&Next {
  //  padding-right: 8px;
  //}
  //&Prev {
  //  padding-left: 8px;
  //}
  &Inactive {
    cursor: default;
    color: var(--color-gray-12);
  }
}

.tabs {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  display: flex;
  z-index: 1;

  &Bordered {
    display: flex;
    align-items: stretch;
    cursor: pointer;
    position: relative;
    @include size('14');
    color: var(--color-gray-08);
    background-color: var(--color-bg-tertiary);
    transition: $transition;
    border-radius: 8px;
    padding: 4px;
    min-height: 36px;
  }
}

.tab {
  &Default {
    color: var(--color-text-secondary);
    text-transform: uppercase;
    padding: 0 16px;
    @include size('12');
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;

    &Active {
      font-weight: 600;
      color: var(--color-text-primary);
      outline: none;

      &:after {
        content: '';
        width: calc(100% - 32px);
        height: 2px;
        background-color: var(--color-text-primary);
        position: absolute;
        bottom: 0;
        left: 16px;
      }
    }
  }

  &Bordered {

    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    text-align: center;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      top: 50%;
      right: 0;
      width: 1px;
      height: 14px;
      margin-top: -7px;
      margin-right: -1px;
      z-index: -1;
      background-color: var(--color-gray-12);
    }

    &:last-child,
    &Active {
      &:after {
        display: none;
      }
    }
    &Active {
      outline: none;
      background-color: var(--color-bg-primary);
      color: var(--color-text-primary);
      font-weight: 600;
    }
  }

  &.invalid {
    color: red;
    &:before {
      display: inline-block;
      content: "";
      line-height: 0;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: red;
      margin-right: 3px;
    }
  }
}

.tabIndicator {
  height: 8px;
  width: 8px;
  background: $color-status-orange;
  border-radius: 100px;
  margin-left: 4px;
  margin-top: -10px;
}
.tabValueInCircle {
  color: var(--b-color-white);
  text-align: center;
  height: 18px;
  width: 18px;
  background: $color-status-red;
  border-radius: 50%;
  margin-left: 4px;
  margin-top: var(--b-gap-0);
}

}</style>
